// /src/components/tools/memberProfileCard/PerkThemeHaloSpiralCanvas.jsx
// Rotating spiral stripes confined to the inner frame border band (never fills the card).

import React, { useEffect, useRef } from 'react';
import { clampPerkThemeHaloSpiral, sanitizePerkThemeHaloColor } from '../../../utils/perkThemeHalo';

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** Point on inset rectangle perimeter, u ∈ [0,1). */
function pointOnInsetRect(u, w, h, inset) {
  const left = inset;
  const top = inset;
  const right = Math.max(left + 1, w - inset);
  const bottom = Math.max(top + 1, h - inset);
  const pw = right - left;
  const ph = bottom - top;
  const perim = 2 * (pw + ph);
  let d = (((u % 1) + 1) % 1) * perim;

  if (d <= pw) return { x: left + d, y: top };
  d -= pw;
  if (d <= ph) return { x: right, y: top + d };
  d -= ph;
  if (d <= pw) return { x: right - d, y: bottom };
  d -= pw;
  return { x: left, y: bottom - d };
}

/** Unit tangent along the perimeter (clockwise). */
function tangentOnInsetRect(u, w, h, inset) {
  const left = inset;
  const top = inset;
  const right = Math.max(left + 1, w - inset);
  const bottom = Math.max(top + 1, h - inset);
  const pw = right - left;
  const ph = bottom - top;
  const perim = 2 * (pw + ph);
  const d = (((u % 1) + 1) % 1) * perim;

  if (d <= pw) return { x: 1, y: 0 }; // top → right
  if (d <= pw + ph) return { x: 0, y: 1 }; // right → down
  if (d <= pw + ph + pw) return { x: -1, y: 0 }; // bottom → left
  return { x: 0, y: -1 }; // left → up
}

/** Inward normal (toward card center). */
function inwardNormal(tangent) {
  // Clockwise tangent → inward is rotate +90°: (x,y) → (-y, x)? 
  // top tangent (1,0) → inward (0,1) down = (y, -x)? (1,0) → (0,1) = (-y, x) wait (-0,1)=(0,1). Yes (-y, x).
  return { x: -tangent.y, y: tangent.x };
}

export default function PerkThemeHaloSpiralCanvas({ color, spiral = 0 }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rotRef = useRef(0);
  const rafRef = useRef(0);

  const safeColor = sanitizePerkThemeHaloColor(color);
  const safeSpiral = clampPerkThemeHaloSpiral(spiral);

  useEffect(() => {
    if (!safeColor || safeSpiral <= 0) return undefined;

    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const rgb = hexToRgb(safeColor);
    const intensity = clamp01(safeSpiral / 100);
    // How fast stripes travel around the rim
    const speed = 0.1 + intensity * 1.15;
    // Stripe count around the perimeter
    const stripeCount = Math.round(22 + intensity * 58);
    const duty = 0.32 + intensity * 0.12;
    // Spiral twist: how much each stripe slants along the rim while crossing the band
    const twist = 0.25 + intensity * 1.35;
    const bandPx = 15;

    let last = performance.now();
    let running = true;

    const resize = () => {
      const w = Math.max(1, wrap.clientWidth || 1);
      const h = Math.max(1, wrap.clientHeight || 1);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.max(1, Math.round(w * dpr));
      const ch = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      rotRef.current += speed * dt;

      const w = Math.max(1, wrap.clientWidth || 1);
      const h = Math.max(1, wrap.clientHeight || 1);
      ctx.clearRect(0, 0, w, h);

      const band = Math.min(bandPx, Math.max(8, Math.min(w, h) * 0.12));
      const rot = rotRef.current;

      // Strict border-only clip — card center stays clear
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      if (w > band * 2 + 2 && h > band * 2 + 2) {
        ctx.rect(band, band, w - band * 2, h - band * 2);
      }
      ctx.clip('evenodd');

      const samples = Math.round(120 + intensity * 220);
      ctx.lineCap = 'round';
      ctx.lineWidth = 1.15 + intensity * 1.25;

      for (let i = 0; i < samples; i += 1) {
        const u0 = i / samples;
        const phase = (((u0 * stripeCount) - rot * stripeCount) % 1 + 1) % 1;
        if (phase > duty) continue;

        // Spiral: stripe starts at outer rim and ends shifted along the perimeter (inward)
        const uOuter = u0;
        const uInner = (u0 + twist / stripeCount) % 1;

        const tanO = tangentOnInsetRect(uOuter, w, h, 1);
        const tanI = tangentOnInsetRect(uInner, w, h, band - 1);
        const inO = inwardNormal(tanO);
        const inI = inwardNormal(tanI);

        const outer = pointOnInsetRect(uOuter, w, h, 1.25);
        const inner = pointOnInsetRect(uInner, w, h, band - 1.25);

        // Soft fade by depth of phase within the duty pulse
        const edge = 1 - Math.abs(phase / duty - 0.5) * 2;
        const alpha = (0.25 + intensity * 0.4) * Math.max(0.2, edge);

        ctx.beginPath();
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
        ctx.moveTo(outer.x + inO.x * 0.5, outer.y + inO.y * 0.5);
        ctx.lineTo(inner.x - inI.x * 0.5, inner.y - inI.y * 0.5);
        ctx.stroke();
      }

      // Secondary finer spiral thread for denser look at higher slider
      if (intensity > 0.2) {
        const fineCount = Math.round(stripeCount * (1.2 + intensity));
        const fineDuty = 0.18 + intensity * 0.1;
        ctx.lineWidth = 0.8 + intensity * 0.7;
        for (let i = 0; i < samples; i += 1) {
          const u0 = (i + 0.5) / samples;
          const phase = (((u0 * fineCount) + rot * fineCount * 0.65) % 1 + 1) % 1;
          if (phase > fineDuty) continue;

          const uInner = (u0 + (twist * 1.4) / fineCount) % 1;
          const outer = pointOnInsetRect(u0, w, h, 2);
          const mid = pointOnInsetRect(uInner, w, h, band * 0.55);
          const alpha = 0.18 + intensity * 0.35;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
          ctx.moveTo(outer.x, outer.y);
          ctx.lineTo(mid.x, mid.y);
          ctx.stroke();
        }
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => resize())
      : null;
    if (ro) ro.observe(wrap);

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) ro.disconnect();
    };
  }, [safeColor, safeSpiral]);

  if (!safeColor || safeSpiral <= 0) return null;

  return (
    <div ref={wrapRef} className="perk-theme-frame-halo perk-theme-frame-halo--spiral" aria-hidden>
      <canvas ref={canvasRef} className="perk-theme-frame-halo__canvas" />
    </div>
  );
}
